import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH_LINGUIST } from '../../../../routes/paths';
// hooks
import useAuth from '../../../../hooks/useAuth';
// portal <-> account-type mapping
import { PORTAL_TYPES } from '../../../../contexts/JWTContext';
// components
import Icon from '../../../../components/universe/Icon';
import FormProvider from '../../../../components/hook-form/FormProvider';
import RHFTextField from '../../../../components/hook-form/RHFTextField';
import {
  authFieldSx,
  authButtonSx,
  authLinkSx
} from '../../../../layouts/auth/portals';

// ----------------------------------------------------------------------
// The sign-in of the vendor portal: email, password, the one verb, then the
// two quiet ways out. Autofill and password managers are told what each
// field is (`autocomplete`, `type`), the phone keyboard opens on the email
// layout, and the reveal toggle names what it does.
// ----------------------------------------------------------------------

/** Supabase's own wording, said for a person at a form. */
function readSignInError(err) {
  const message = err?.message || '';
  if (/not confirmed/i.test(message)) {
    return 'Confirm your email address first — check your inbox for the link we sent when you registered.';
  }
  if (/invalid login credentials/i.test(message)) {
    return 'That email and password do not match. Check both and try again, or reset your password below.';
  }
  return message || 'We could not sign you in. Please try again.';
}

export default function LoginForm() {
  const { signInWithPassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [wrongPortal, setWrongPortal] = useState(null);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Enter a valid email address')
      .required('Enter your email address'),
    password: Yup.string().required('Enter your password')
  });

  const defaultValues = {
    email: '',
    password: ''
  };

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(LoginSchema),
    defaultValues
  });

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods;

  const onSubmit = async (data) => {
    setAuthError('');
    setWrongPortal(null);
    try {
      await signInWithPassword(data.email, data.password, PORTAL_TYPES.vendors);
    } catch (err) {
      setAuthError(readSignInError(err));
      // Valid credentials for another portal: offer the way there.
      if (err?.portalPath) {
        setWrongPortal({ path: err.portalPath, label: err.portalLabel });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {authError && (
          <Alert
            severity={wrongPortal ? 'info' : 'error'}
            role="alert"
            action={
              wrongPortal ? (
                <Link
                  component={RouterLink}
                  to={wrongPortal.path}
                  underline="hover"
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Go to {wrongPortal.label}
                </Link>
              ) : null
            }
          >
            {authError}
          </Alert>
        )}

        <RHFTextField
          sx={authFieldSx}
          name="email"
          label="Email address"
          type="email"
          inputProps={{
            autoComplete: 'email',
            inputMode: 'email',
            autoCapitalize: 'none',
            spellCheck: 'false'
          }}
        />

        <RHFTextField
          name="password"
          label="Password"
          sx={authFieldSx}
          type={showPassword ? 'text' : 'password'}
          inputProps={{ autoComplete: 'current-password' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  <Icon name={showPassword ? 'EyeSlash' : 'Eye'} size="md" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={(theme) => ({ ...authButtonSx(theme), mt: 3 })}
      >
        <span className="notranslate" translate="no">
          Sign in
        </span>
      </LoadingButton>

      {/* Each alternative is its own page, so this screen keeps a single
          primary action and neither flow shares state with the form. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2.5 }}
      >
        <Link
          component={RouterLink}
          to={PATH_AUTH_LINGUIST.magicLink}
          underline="hover"
          sx={authLinkSx}
        >
          Sign in with a link
        </Link>
        <Link
          component={RouterLink}
          to={PATH_AUTH_LINGUIST.resetPassword}
          underline="hover"
          sx={(theme) => authLinkSx(theme, { quiet: true })}
        >
          Forgot password?
        </Link>
      </Stack>
    </FormProvider>
  );
}
